import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { updateUserSettings } from '@disco/state-management/actions/UserActions'
import captureError, { parseServerError } from '@disco/utils/captureError'

import type { AudioSeparationDialogState } from 'types/audio-separation'
import type { HttpErrorType } from 'types/errors'

import Alert from 'components/ui/alert'
import ModalDialog from 'components/ui/modal-dialog'
import Stack from 'components/ui/stack'

import { useDispatch } from 'hooks/use-redux'

const AudioSeparationTermsDialog = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as AudioSeparationDialogState | null
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMountedRef = useRef(false)

  // Default separationType to 'instrumental' when arriving from the legacy
  // /explorer/instant-instrumental-terms-dialog route (StatePreservingRedirect
  // preserves the old state which lacks separationType).
  // If no state at all, redirect home (direct navigation or bookmarked URL).
  useEffect(() => {
    if (!state) {
      navigate('/', { replace: true })
    } else if (!state.separationType) {
      // Migrate legacy state by adding the missing separationType
      navigate(location.pathname, {
        replace: true,
        state: { ...state, separationType: 'instrumental' as const },
      })
    }
  }, [state, navigate, location.pathname])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleCancel = useCallback(() => {
    navigate(state?.originalReturnTo ?? state?.returnTo ?? '/')
  }, [navigate, state])

  const onSubmit = useCallback(async () => {
    // Guard against missing state (should be caught by useEffect, but defensive)
    if (!state?.separationType) {
      navigate('/', { replace: true })
      return
    }

    setError(null)
    setSubmitting(true)

    try {
      // Only persist terms acceptance if the user has not already accepted.
      // Use explicit !== true to avoid re-persisting when termsAccepted is undefined.
      if (state.termsAccepted !== true) {
        await dispatch(
          updateUserSettings(
            {
              settings: {
                audio_separation_terms_accepted_at: new Date().toISOString(),
              },
            },
            false,
          ),
        )
      }

      if (!isMountedRef.current) return

      navigate(state.returnTo ?? '/', {
        replace: true,
        state: {
          returnTo: state.originalReturnTo ?? state.returnTo ?? '/',
          originalReturnTo: state.originalReturnTo,
          separationType: state.separationType,
          autoTagged: state.autoTagged,
          termsAccepted: true,
        } satisfies AudioSeparationDialogState,
      })
    } catch (error: unknown) {
      if (!isMountedRef.current) return

      const httpError = error as HttpErrorType
      setError(
        parseServerError(
          httpError?.body?.[0] || httpError?.body || httpError?.message || null,
        ),
      )
      setSubmitting(false)
      dispatch(sendGenericError(error as Error))
      captureError(error as Error)
    }
  }, [dispatch, navigate, state])

  if (!state || !state.separationType) return null

  return (
    <ModalDialog
      title="Audio Separation Terms and Conditions"
      width="large"
      primaryAction={{
        caption: 'Accept',
        onClick: onSubmit,
        progressCaption: 'Submitting',
        progressIndicator: submitting,
      }}
      onCancel={handleCancel}
    >
      <Stack space="small">
        <Alert status="info" icon="info">
          Note: We have updated our Audio Separation terms to include our new A
          Cappella tool. You will not be able to proceed to use this Feature
          without clicking Accept.
        </Alert>
        <div>
          As a condition for accessing audio separation features, you understand and agree that, either: (1) you are using
          work that you own the right to; (2) you are using work that you have
          been authorized to alter or modify by the rights holder(s); or (3) you
          intend to obtain the appropriate legal rights from the relevant rights
          holder(s) before using this work for any commercial purpose or
          personal or professional gain. Any other use of this Feature shall be
          considered a violation of our Terms and Conditions and not permitted
          on our platform. Please review DISCO’s{' '}
          <a
            className="text-link"
            href="https://disco.ac/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and conditions
          </a>{' '}
          if you have any additional questions on the legal use of this
          platform.
        </div>
        {error && (
          <div>
            <Alert status="danger" icon="warning">
              {String(error)}
            </Alert>
          </div>
        )}
      </Stack>
    </ModalDialog>
  )
}

export default AudioSeparationTermsDialog
